import { Show } from "solid-js";
import { persistantStore } from "@store/persistantStore";
import { isLoggedIn } from "@store/userStore";
import { isProjectUnderMaintenance } from "@utils/projectStatus";

const { selectedProjectId, projectStatus, importRunActive, importRunLabel } = persistantStore;

/**
 * "Data loading in progress" notice for a server-side dataset import.
 *
 * A dataset import now runs on the server, so whoever started it can close their laptop. That makes it
 * possible for everyone else to be looking at a project whose data is still being built — hence a
 * notice visible to all users rather than only the person who kicked it off. Admins get the per-step
 * detail on Admin -> Script Execution.
 *
 * Rendered as a `position: fixed` overlay, NOT an in-flow strip: Styles.css has ~80 hard-coded
 * `calc(100vh - 60px)` rules keyed to the 60px header, so anything occupying layout space here pushes
 * every scaled page down and breaks its fit-to-height maths.
 *
 * No dismiss button, unlike the maintenance notice: this state clears itself when the import finishes,
 * and it is short-lived by nature. It also yields to the maintenance banner — both are fixed at
 * `top: 60px`, so rendering both would overlap them.
 *
 * State comes from persistantStore, refreshed on the existing 30s project-meta poll. The tab that
 * started the import shows its own live progress instead; this is for everyone else.
 */
export default function ImportRunBanner() {
  return (
    <Show
      when={
        isLoggedIn() &&
        selectedProjectId() > 0 &&
        importRunActive() &&
        !isProjectUnderMaintenance(projectStatus())
      }
    >
      <div class="import-banner" role="status" aria-live="polite">
        <svg
          class="import-banner-icon"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M12 3A9 9 0 1 0 21 12"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
          />
        </svg>
        <span class="import-banner-label">Data loading</span>
        <span class="import-banner-message">{importRunLabel() || "Dataset import in progress"}</span>
      </div>
    </Show>
  );
}
